import { OBJ } from '../constants';
import { readTextLines } from './pdfEngine';

/**
 * The accessibility check.
 *
 * Word checks a document against what a screen reader needs. A PDF needs the
 * same things, and in this country it usually fails the same way: a page that
 * is a photograph of a page, with no text in it at all. That is the first
 * thing this looks for, because no amount of alt text fixes it.
 *
 * Every issue names what and where, so the panel can go to it.
 */

/** WCAG's contrast floor for body text, and the looser one large type gets. */
const CONTRAST_AA = 4.5;
const CONTRAST_AA_LARGE = 3;
const LARGE_TEXT_PT = 18;
/** Below this, type is unreadable on paper whatever its contrast. */
const TINY_TEXT_PT = 6;

const channel = (value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

function luminance(hex) {
    const clean = String(hex || '#000').replace('#', '');
    const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    const r = parseInt(full.slice(0, 2), 16) || 0;
    const g = parseInt(full.slice(2, 4), 16) || 0;
    const b = parseInt(full.slice(4, 6), 16) || 0;
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(foreground, background) {
    const a = luminance(foreground);
    const b = luminance(background);
    const light = Math.max(a, b);
    const dark = Math.min(a, b);
    return (light + 0.05) / (dark + 0.05);
}

/**
 * @returns {Promise<{ id, kind, severity, pageIndex, objectId }[]>}
 *   `kind` names the problem; the panel turns it into words.
 */
export async function auditDocument({ pages, objectsByUid, pdf, language, title }) {
    const issues = [];
    const push = (kind, severity, pageIndex, objectId = null, detail = null) => {
        issues.push({ id: `${kind}-${pageIndex}-${objectId || issues.length}`, kind, severity, pageIndex, objectId, detail });
    };

    if (!language) push('noLanguage', 'warning', 0);
    if (!title?.trim()) push('noTitle', 'warning', 0);

    for (const [pageIndex, page] of pages.entries()) {
        const objects = objectsByUid[page.uid] || [];

        // A scanned page: a picture of words is not words. A screen reader gets
        // nothing at all from it, and no other fix here changes that.
        if (page.sourceIndex != null && pdf) {
            // eslint-disable-next-line no-await-in-loop
            const lines = await readTextLines(pdf, page.sourceIndex + 1);
            const addedText = objects.some((o) => o.type === OBJ.TEXT && o.text?.trim());
            if (!lines.length && !addedText) push('imageOnlyPage', 'error', pageIndex);
        }

        // Text on a page sits on white: the editor has no page colour of its own.
        const pageFill = '#FFFFFF';

        for (const obj of objects) {
            if (obj.type === OBJ.IMAGE && !obj.alt?.trim()) {
                push('imageNoAlt', 'error', pageIndex, obj.id);
            }

            if (obj.type === OBJ.LINK && !obj.url && !Number.isInteger(obj.targetPage)) {
                push('linkNoTarget', 'warning', pageIndex, obj.id);
            }

            if (obj.type === OBJ.TEXT && obj.text?.trim()) {
                const size = obj.fontSize || 12;
                if (size < TINY_TEXT_PT) push('tinyText', 'warning', pageIndex, obj.id, { size });

                const background = obj.background && obj.background !== 'transparent'
                    ? obj.background
                    : pageFill;
                const ratio = contrastRatio(obj.color || '#000000', background);
                const floor = size >= LARGE_TEXT_PT || (size >= 14 && obj.bold)
                    ? CONTRAST_AA_LARGE
                    : CONTRAST_AA;
                if (ratio < floor) {
                    push('lowContrast', 'error', pageIndex, obj.id, {
                        ratio: Math.round(ratio * 10) / 10,
                        needed: floor,
                    });
                }
            }
        }
    }

    return issues;
}
